/**
 * Files guide — generating, listing, downloading, and uploading files in runs.
 *
 * Covers instructing teammates to write files, listing run files via the API,
 * downloading generated output, and the upcoming file upload feature.
 */

export const filesContent = `
Teammates can generate files during runs — CSV reports, JSON exports, code files, and more. Instruct your teammate to write files and download them through the API.

## How It Works

1. Include file output instructions in your task (e.g. "write a CSV report")
2. The teammate writes files during execution using its built-in file write tool
3. List and download files via the API after the run completes

## Get Generated Files

### Generating Files

Tell the teammate what to produce in the task instructions:

\`\`\`python
from m8tes import M8tes

client = M8tes()

run = client.runs.create(
    teammate_id=1,
    message="Analyze our open support tickets and write a CSV summary to tickets_report.csv",
    stream=False,
)
run = client.runs.poll(run.id)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Write a JSON export of all VIP customers to vip_export.json", "stream": false}'
\`\`\`

### List Run Files

After a run completes, list the files it generated:

\`\`\`python
files = client.runs.list_files(run.id)
for f in files:
    print(f.name, f.size)
\`\`\`

\`\`\`bash
curl https://m8tes.ai/api/v2/runs/1/files \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

### Download Files

\`\`\`python
content = client.runs.download_file(run.id, filename="tickets_report.csv")
with open("tickets_report.csv", "wb") as f:
    f.write(content)
\`\`\`

\`\`\`bash
curl https://m8tes.ai/api/v2/runs/1/files/tickets_report.csv/download \\
  -H "Authorization: Bearer m8_your_key_here" -o tickets_report.csv
\`\`\`

### Supported File Types

Agents can write any text-based file format:

| Type | Examples |
|------|----------|
| Data | CSV, TSV, JSON, JSONL |
| Documents | Markdown, plain text, HTML |
| Code | Python, JavaScript, SQL, YAML |
| Reports | Formatted text summaries |

### Tips for File Output

Be specific in your task instructions:

\`\`\`python
# Good — specific filename and format
run = client.runs.create(
    teammate_id=1,
    message="Query open tickets, then write a CSV with columns: id, subject, priority. Save as tickets.csv",
    stream=False,
)

# Good — multiple files
run = client.runs.create(
    teammate_id=1,
    message="""Analyze campaign performance:
1. Write a summary report to campaign_report.md
2. Export raw metrics to metrics.csv
3. Save the top 10 campaigns as top_campaigns.json""",
    stream=False,
)
\`\`\`

### Scheduled File Generation

Combine files with scheduling to produce regular reports:

\`\`\`python
task = client.tasks.create(
    teammate_id=1,
    instructions="Generate the weekly performance report as weekly_report.csv and email it to team@acme.com"
)
trigger = client.tasks.triggers.create(task.id, type="schedule", cron="0 9 * * 1", timezone="America/New_York")
\`\`\`

## Attach Files to Runs (Coming Soon)

Direct file upload to provide context for runs (spreadsheets, documents, images) is coming soon. This will let you attach files when creating a run so the teammate can read and process them.

## What's Next

- [Runs](/docs/runs) — run execution and output
- [Scheduling](/docs/scheduling) — automate file generation on a recurring schedule
- [Streaming & Events](/docs/streaming) — monitor file generation progress in real-time
`;
